import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
} from '@mui/material';
import {
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore,
  ChevronRight,
} from '@mui/icons-material';
import { FileSystemNode, FileSystemMetadata } from '../types/api';

interface FolderSelectorDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (folderId: string) => void;
  fileSystem: FileSystemMetadata | null;
  currentNodeId: string; // The node being moved (to exclude it and its descendants)
  currentParentId?: string; // Current parent of the node (to show where it is now)
}

interface FolderTreeNodeProps {
  node: FileSystemNode;
  nodes: Record<string, FileSystemNode>;
  level: number;
  selectedFolderId: string | null;
  onSelectFolder: (folderId: string) => void;
  excludedNodeId: string;
  currentParentId?: string;
  isDescendantOf: (nodeId: string, ancestorId: string) => boolean;
}

const FolderTreeNode: React.FC<FolderTreeNodeProps> = ({
  node,
  nodes,
  level,
  selectedFolderId,
  onSelectFolder,
  excludedNodeId,
  currentParentId,
  isDescendantOf,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Don't show if this is the node being moved or a descendant of it
  if (node.id === excludedNodeId || isDescendantOf(node.id, excludedNodeId)) {
    return null;
  }

  // Only show folders
  if (node.type !== 'folder') {
    return null;
  }

  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedFolderId === node.id;
  const isCurrent = currentParentId === node.id;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleSelect = () => {
    onSelectFolder(node.id);
  };

  const children = hasChildren
    ? node.children!
        .map(childId => nodes[childId])
        .filter(child => child && child.type === 'folder')
    : [];

  // Sort folders alphabetically
  children.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: level * 2,
        }}
      >
        <ListItemButton
          onClick={handleSelect}
          selected={isSelected}
          sx={{
            borderRadius: 1,
            '&.Mui-selected': {
              backgroundColor: 'primary.light',
              '&:hover': {
                backgroundColor: 'primary.light',
              },
            },
          }}
        >
          {hasChildren && (
            <ListItemIcon 
              sx={{ minWidth: 32, cursor: 'pointer' }}
              onClick={handleToggle}
            >
              {isExpanded ? <ExpandMore /> : <ChevronRight />}
            </ListItemIcon>
          )}
          {!hasChildren && <div style={{ width: 32 }} />}
          <ListItemIcon sx={{ minWidth: 36 }}>
            {isExpanded ? (
              <FolderOpenIcon color="primary" />
            ) : (
              <FolderIcon color="primary" />
            )}
          </ListItemIcon>
          <ListItemText
            primary={node.name}
            secondary={isCurrent ? '(current location)' : undefined}
            primaryTypographyProps={{
              variant: 'body2',
              sx: { fontWeight: 500 },
            }}
            secondaryTypographyProps={{
              variant: 'caption',
              sx: { fontStyle: 'italic' },
            }}
          />
        </ListItemButton>
      </ListItem>

      {/* Children */}
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((child) => (
              <FolderTreeNode
                key={child.id}
                node={child}
                nodes={nodes}
                level={level + 1}
                selectedFolderId={selectedFolderId}
                onSelectFolder={onSelectFolder}
                excludedNodeId={excludedNodeId}
                currentParentId={currentParentId}
                isDescendantOf={isDescendantOf}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const FolderSelectorDialog: React.FC<FolderSelectorDialogProps> = ({
  open,
  onClose,
  onSelect,
  fileSystem,
  currentNodeId,
  currentParentId,
}) => {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  // Reset selection when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedFolderId(null);
    }
  }, [open]);

  const handleSelectFolder = (folderId: string) => {
    setSelectedFolderId(folderId);
  };

  const handleConfirm = () => {
    if (selectedFolderId) {
      onSelect(selectedFolderId);
      setSelectedFolderId(null);
    }
  };

  const handleCancel = () => {
    setSelectedFolderId(null);
    onClose();
  };

  // Helper function to check if a node is a descendant of another
  const isDescendantOf = (nodeId: string, ancestorId: string): boolean => {
    if (!fileSystem || !fileSystem.nodes) return false;
    
    let currentId: string | undefined = nodeId;
    const visited = new Set<string>();
    
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const currentNode: FileSystemNode | undefined = fileSystem.nodes[currentId];
      if (!currentNode) break;
      
      if (currentNode.parentId === ancestorId) {
        return true;
      }
      currentId = currentNode.parentId;
    }
    
    return false;
  };

  if (!fileSystem || !fileSystem.nodes) {
    return null;
  }

  const rootNode = fileSystem.nodes[fileSystem.rootId];
  if (!rootNode) {
    return null;
  }

  const rootChildren = rootNode.children
    ? rootNode.children
        .map(childId => fileSystem.nodes[childId])
        .filter(child => child && child.type === 'folder')
    : [];

  // Sort folders alphabetically
  rootChildren.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Move to Folder</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select a destination folder:
        </Typography>
        <List sx={{ 
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: '400px',
          overflowY: 'auto',
        }}>
          {/* Root folder option */}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => handleSelectFolder(fileSystem.rootId)}
              selected={selectedFolderId === fileSystem.rootId}
              sx={{
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <div style={{ width: 32 }} />
              <ListItemIcon sx={{ minWidth: 36 }}>
                <FolderIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Root"
                secondary={currentParentId === fileSystem.rootId ? '(current location)' : undefined}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { fontWeight: 500 },
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  sx: { fontStyle: 'italic' },
                }}
              />
            </ListItemButton>
          </ListItem>

          {/* Child folders */}
          {rootChildren.map((child) => (
            <FolderTreeNode
              key={child.id}
              node={child}
              nodes={fileSystem.nodes}
              level={1}
              selectedFolderId={selectedFolderId}
              onSelectFolder={handleSelectFolder}
              excludedNodeId={currentNodeId}
              currentParentId={currentParentId}
              isDescendantOf={isDescendantOf}
            />
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button 
          onClick={handleConfirm} 
          variant="contained"
          disabled={!selectedFolderId || selectedFolderId === currentParentId}
        >
          Move Here
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FolderSelectorDialog;
